import { FunctionComponent, JSX, useEffect, useState } from 'react'
import { TaskNote, TaskNoteStatus, TaskNoteType } from '@/types/TaskNote.ts'
import styles from './taskNoteList.module.scss'
import { useTranslation } from 'react-i18next'
import DatePicker from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import moment from 'moment/moment'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { ResetTv, Search } from '@mui/icons-material'
import { StaffWithLinks } from '@/types/Staff.ts'
import { Child, ChildDaycare, ChildDaycareWithParents, ChildWithParents } from '@/types/Child.ts'
import { Parent } from '@/types/Parent.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import { useLazyQuery } from '@apollo/client'
import {
    GET_TASK_NOTES_BY_CHILDS,
    GET_TASK_NOTES_BY_DAYCARE,
    GET_TASK_NOTES_BY_PARENTS,
    GET_TASK_NOTES_BY_STAFFS
} from '@/modules/API/taskNote/getTaskNotes.ts'
import Loading from '@/components/Loading/Loading.tsx'
import TaskNoteComponent from '../taskNoteComponent/taskNoteComponent.tsx'
import Title from '@/components/_custom/Title/title.tsx'
import AddTasksNotesDialog from '../Dialog/addTasksNotesDialog.tsx'
import { sortTaskNote } from '@/modules/helper/taskNoteHelper.ts'
import Mobile from '@/modules/desktopMobile/mobile.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface TaskNoteListProps {
    fromDate: string | null
    toDate: string | null
    parentId: string | null
    childId: string | null
    staffId: string | null
    staffList: StaffWithLinks[]
    childList: ChildWithParents[]
    parentsList: Parent[]
    daycareId: number
    isRpeOrLaep: boolean
    canUpdate: boolean
    typeFilters: string[]
    statusFilters: string[]
    setTypeFilters: (value: string[]) => void
    handleDataLoaded: (value: TaskNote[]) => void
    resetFilter: () => void
}

const TaskNoteList: FunctionComponent<TaskNoteListProps> = ({
    fromDate,
    toDate,
    parentId,
    childId,
    staffId,
    staffList,
    childList,
    parentsList,
    daycareId,
    isRpeOrLaep,
    canUpdate,
    typeFilters,
    statusFilters,
    setTypeFilters,
    handleDataLoaded,
    resetFilter
}: TaskNoteListProps) => {
    const { t } = useTranslation()

    const { showNotificationError } = useNotification()

    const [tasksNotesList, setTaskNotesList] = useState<TaskNote[]>([])
    const [showingTasksNotesList, setShowingTaskNotesList] = useState<TaskNote[]>([])

    const [fromDateSelected, setFromDateSelected] = useState<string>(fromDate || moment().format('DD-MM-YYYY'))
    const [toDateSelected, setToDateSelected] = useState<string>(
        toDate || moment().add(1, 'month').format('DD-MM-YYYY')
    )

    const [parentIdList, setParentIdList] = useState<string[]>(
        parentId && parentIdIsInList(parentId, parentsList) ? [parentId] : []
    )
    const [childIdList, setChildIdList] = useState<string[]>(
        childId && childIdIsInList(childId, childList) ? [childId] : []
    )
    const [staffIdList, setStaffIdList] = useState<string[]>(
        staffId && staffIdIsInList(staffId, staffList) ? [staffId] : []
    )

    const [isAddTaskNoteDialogOpen, setAddTaskNoteDialogOpen] = useState<boolean>(false)

    const childOptions: SelectOptionsDeprecated = [
        {
            options: childList
                .sort((a: ChildWithParents, b: ChildWithParents): number => {
                    return a.firstname.localeCompare(b.firstname)
                })
                .map((child: ChildWithParents): SelectOptionDeprecated => {
                    const complement: string | undefined = child.daycares.find(
                        (d: ChildDaycareWithParents): boolean => d.daycareId === daycareId
                    )?.complement
                    return {
                        name: `${child.firstname}${complement ? ` - ${complement}` : ''}`,
                        value: child.id.toString()
                    }
                })
        }
    ]

    const parentOptions: SelectOptionsDeprecated = [
        {
            options: parentsList
                .sort((a: Parent, b: Parent): number => {
                    return a.lastname.localeCompare(b.lastname)
                })
                .map(
                    (parent: Parent): SelectOptionDeprecated => ({
                        name:
                            (parent.firstname + ' ' + parent.lastname).trim() === ''
                                ? parent.email
                                : parent.firstname + ' ' + parent.lastname,
                        value: parent.id.toString()
                    })
                )
        }
    ]

    const staffOptions: SelectOptionsDeprecated = [
        {
            options: staffList
                .sort((a: StaffWithLinks, b: StaffWithLinks): number => {
                    return a.lastname.localeCompare(b.lastname)
                })
                .map((staff: StaffWithLinks): SelectOptionDeprecated => {
                    return {
                        name: staff.firstname + ' ' + staff.lastname,
                        value: staff.id.toString()
                    }
                })
        }
    ]

    const filterTypeOptions: SelectOptionsDeprecated = [
        {
            options: Object.keys(TaskNoteType).map((type: string): SelectOptionDeprecated => {
                return {
                    value: type,
                    name: t(type)
                }
            })
        }
    ]

    const [getTasksNotesChild, { loading: loadingChilds, data: dataChilds, error: errorChilds }] = useLazyQuery(
        GET_TASK_NOTES_BY_CHILDS,
        {
            variables: {
                daycareId: daycareId,
                childIds: childIdList.map((id: string): number => Number(id)),
                from: fromDateSelected,
                to: toDateSelected
            }
        }
    )

    useEffect(() => {
        if (dataChilds?.getTaskNotesByChild) {
            setTaskNotesList((prev: TaskNote[]): TaskNote[] => {
                const newItems: TaskNote[] = dataChilds.getTaskNotesByChild.filter(
                    (note: TaskNote): boolean => !prev.some((existing: TaskNote): boolean => existing.id === note.id)
                )
                return [...prev, ...newItems]
            })
            handleDataLoaded(dataChilds.getTaskNotesByChild)
        }
    }, [dataChilds])

    const [getTasksNotesParent, { loading: loadingParents, data: dataParents, error: errorParents }] = useLazyQuery(
        GET_TASK_NOTES_BY_PARENTS,
        {
            variables: {
                daycareId: daycareId,
                parentIds: parentIdList.map((id: string): number => Number(id)),
                from: fromDateSelected,
                to: toDateSelected
            }
        }
    )

    useEffect(() => {
        if (dataParents?.getTaskNotesByParent) {
            setTaskNotesList((prev: TaskNote[]): TaskNote[] => {
                const newItems: TaskNote[] = dataParents.getTaskNotesByParent.filter(
                    (note: TaskNote): boolean => !prev.some((existing: TaskNote): boolean => existing.id === note.id)
                )
                return [...prev, ...newItems]
            })
            handleDataLoaded(dataParents.getTaskNotesByParent)
        }
    }, [dataParents])

    const [getTasksNotesStaff, { loading: loadingStaffs, data: dataStaffs, error: errorStaffs }] = useLazyQuery(
        GET_TASK_NOTES_BY_STAFFS,
        {
            variables: {
                daycareId: daycareId,
                staffIds: staffIdList.map((id: string): number => Number(id)),
                from: fromDateSelected,
                to: toDateSelected
            }
        }
    )

    useEffect(() => {
        if (dataStaffs?.getTaskNotesByStaff) {
            setTaskNotesList((prev: TaskNote[]): TaskNote[] => {
                const newItems: TaskNote[] = dataStaffs.getTaskNotesByStaff.filter(
                    (note: TaskNote): boolean => !prev.some((existing: TaskNote): boolean => existing.id === note.id)
                )
                return [...prev, ...newItems]
            })
            handleDataLoaded(dataStaffs.getTaskNotesByStaff)
        }
    }, [dataStaffs])

    const [getTasksNotesDaycare, { loading: loadingDaycare, data: dataDaycare, error: errorDaycare }] = useLazyQuery(
        GET_TASK_NOTES_BY_DAYCARE,
        {
            variables: {
                daycareId: daycareId,
                from: fromDateSelected,
                to: toDateSelected
            }
        }
    )

    useEffect(() => {
        if (dataDaycare?.getTaskNotesByDaycare) {
            setTaskNotesList((prev: TaskNote[]): TaskNote[] => {
                const newItems: TaskNote[] = dataDaycare.getTaskNotesByDaycare.filter(
                    (note: TaskNote): boolean => !prev.some((existing: TaskNote): boolean => existing.id === note.id)
                )
                return [...prev, ...newItems]
            })
            handleDataLoaded(dataDaycare.getTaskNotesByDaycare)
        }
    }, [dataDaycare])

    useEffect(() => {
        const message: string | undefined =
            errorDaycare?.message || errorChilds?.message || errorParents?.message || errorStaffs?.message

        if (message === '1 year max between from and to date') {
            showNotificationError(t('errorGetTaskNotes1Year'))
        } else if (errorDaycare || errorChilds || errorParents || errorStaffs) {
            showNotificationError(t('errorGetTaskNotes'))
        }
    }, [errorDaycare, errorChilds, errorParents, errorStaffs])

    useEffect(() => {
        setChildIdList([])
        setParentIdList([])
        setStaffIdList([])

        if (childId && childList.length > 0 && childIdIsInList(childId, childList)) setChildIdList([childId])
        if (parentId && parentsList.length > 0 && parentIdIsInList(parentId, parentsList)) setParentIdList([parentId])
        if (staffId && staffList.length > 0 && staffIdIsInList(staffId, staffList)) setStaffIdList([staffId])
    }, [daycareId, childList, parentsList, staffList, staffId, childId, parentId])

    useEffect(() => {
        setTaskNotesList([])
    }, [daycareId])

    useEffect(() => {
        if (childId) getTasksNotesChild()
        if (parentId) getTasksNotesParent()
        if (staffId) getTasksNotesStaff()

        if (!childId && !parentId && !staffId) getTasksNotesDaycare()
    }, [childId, staffId, parentId])

    function handleRefetch(): void {
        if (childIdList.length > 0 || parentIdList.length > 0 || staffIdList.length > 0) {
            if (childIdList.length > 0) getTasksNotesChild()
            if (parentIdList.length > 0) getTasksNotesParent()
            if (staffIdList.length > 0) getTasksNotesStaff()
        } else {
            getTasksNotesDaycare()
        }
    }

    function handleSearch(): void {
        setTaskNotesList([])

        if (childIdList.length > 0 || parentIdList.length > 0 || staffIdList.length > 0) handleRefetch()
        else getTasksNotesDaycare()
    }

    useEffect(() => {
        const tempTaskNotesList: TaskNote[] = tasksNotesList.filter((taskNote: TaskNote): boolean => {
            // CAS PARTICULIER
            if (taskNote.status) {
                if (
                    taskNote.status === TaskNoteStatus.done &&
                    (taskNote.type === TaskNoteType.appointmentInPerson ||
                        taskNote.type === TaskNoteType.appointmentVideoconference) &&
                    typeFilters.includes(taskNote.type) &&
                    statusFilters.includes(TaskNoteStatus.done + ' ' + taskNote.type)
                ) {
                    return true
                }
                return typeFilters.includes(taskNote.type) && statusFilters.includes(taskNote.status)
            } else {
                return typeFilters.includes(taskNote.type)
            }
        })

        setShowingTaskNotesList([])
        setShowingTaskNotesList(tempTaskNotesList)
    }, [typeFilters, tasksNotesList, statusFilters])

    return (
        <div className={styles.TaskNoteListPlacement}>
            {(loadingChilds || loadingStaffs || loadingParents || loadingDaycare) && <Loading overall />}
            <div className={styles.Header}>
                <div className={styles.InputContainer}>
                    <DatePicker
                        value={fromDateSelected}
                        setValue={setFromDateSelected}
                        label={t('fromDate')}
                        disablePast={false}
                        className={styles.Input}
                    />
                    <DatePicker
                        value={toDateSelected}
                        setValue={setToDateSelected}
                        label={t('toDate')}
                        disablePast={false}
                        minDate={fromDateSelected}
                        maxDate={moment(fromDateSelected, 'DD-MM-YYYY').add(1, 'year').format('DD-MM-YYYY')}
                        className={styles.Input}
                    />
                </div>
                <div className={styles.InputContainer}>
                    <SelectMultiple
                        label={t('childs')}
                        selectOptions={childOptions}
                        selectValues={childIdList}
                        setSelectValues={setChildIdList}
                        className={styles.Input}
                    />
                    <SelectMultiple
                        label={t(isRpeOrLaep ? 'publics' : 'parents')}
                        selectOptions={parentOptions}
                        selectValues={parentIdList}
                        setSelectValues={setParentIdList}
                        className={styles.Input}
                    />
                    <SelectMultiple
                        label={t('staffs')}
                        selectOptions={staffOptions}
                        selectValues={staffIdList}
                        setSelectValues={setStaffIdList}
                        className={styles.Input}
                    />
                </div>
                <div className={styles.InputContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Search />}
                        customText={t('search')}
                        onClick={handleSearch}
                        className={styles.Button}
                    />
                </div>
            </div>
            <Mobile>
                <div className={styles.Header}>
                    <div className={styles.InputContainer}>
                        <SelectMultiple
                            label={t('filter')}
                            selectOptions={filterTypeOptions}
                            selectValues={typeFilters}
                            setSelectValues={setTypeFilters}
                            className={styles.Input}
                        />
                    </div>
                </div>
            </Mobile>
            <div className={styles.Form}>
                <div className={styles.TitleAndButton}>
                    <div className={styles.Title}>
                        <Title text={t('tasksAndNotes')} />
                    </div>
                    {canUpdate && (
                        <ButtonTextIcon
                            type={'add'}
                            onClick={() => setAddTaskNoteDialogOpen(true)}
                            disabled={isAddTaskNoteDialogOpen}
                            className={styles.Button}
                        />
                    )}
                </div>
                {showingTasksNotesList.length > 0 ? (
                    <div className={styles.Container}>
                        {showingTasksNotesList.sort(sortTaskNote).map((taskNote: TaskNote): JSX.Element => {
                            return (
                                <div key={taskNote.id} className={styles.TaskNoteContainer}>
                                    <TaskNoteComponent
                                        taskNote={taskNote}
                                        isParent={false}
                                        isRpeOrLaep={isRpeOrLaep}
                                        canUpdate={canUpdate}
                                        daycareId={daycareId}
                                        handleRefetch={handleSearch}
                                        childs={convertChildWithParentsToChild(childList)}
                                        parents={parentsList}
                                        staffs={staffList}
                                    />
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className={styles.Container}>
                        <p className={styles.NoTaskNote}>{t('noTasksAndNotes')}</p>
                        {tasksNotesList.length > 0 && (
                            <ButtonTextIcon
                                className={styles.Button}
                                type={'custom'}
                                customIcon={<ResetTv />}
                                customText={t('resetFilter')}
                                onClick={resetFilter}
                            />
                        )}
                    </div>
                )}
            </div>
            {isAddTaskNoteDialogOpen && (
                <AddTasksNotesDialog
                    open={isAddTaskNoteDialogOpen}
                    handleClose={() => setAddTaskNoteDialogOpen(false)}
                    daycareId={daycareId}
                    handleRefetch={handleRefetch}
                    isRpeOrLaep={isRpeOrLaep}
                    childs={convertChildWithParentsToChild(childList)}
                    parents={parentsList}
                    staffs={staffList}
                />
            )}
        </div>
    )
}

export default TaskNoteList

function parentIdIsInList(parentId: string, parentsList: Parent[]): boolean {
    return parentsList.some((parent: Parent): boolean => parent.id === Number(parentId))
}

function childIdIsInList(childId: string, childList: ChildWithParents[]): boolean {
    return childList.some((child: ChildWithParents): boolean => child.id === Number(childId))
}

function staffIdIsInList(staffId: string, staffList: StaffWithLinks[]): boolean {
    return staffList.some((staff: StaffWithLinks): boolean => staff.id === Number(staffId))
}

function convertChildWithParentsToChild(childList: ChildWithParents[]): Child[] {
    return childList.map((child: ChildWithParents): Child => {
        return {
            id: child.id,
            firstname: child.firstname,
            birthdate: moment(child.birthdate, 'DD-MM-YYYY').format('DD-MM-YYYY'),
            daycares: convertChildDaycareWithParentsToChildDaycare(child.daycares),
            mon: 0,
            tue: 0,
            wed: 0,
            thu: 0,
            fri: 0,
            sat: 0,
            sun: 0
        }
    })
}

function convertChildDaycareWithParentsToChildDaycare(childDaycares: ChildDaycareWithParents[]): ChildDaycare[] {
    const res: ChildDaycare[] = []

    childDaycares.forEach((childDaycare: ChildDaycareWithParents): void => {
        res.push({
            daycareId: childDaycare.daycareId,
            sections: [],
            complement: childDaycare.complement || '',
            request: false,
            visible: childDaycare.visible
        })
    })

    return res
}
